import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { EMPTY, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LocalstorageService } from '@services/localstorage.service';
import { CacheService } from '@services/cache.service';
import { SessionService } from '@services/session.service';
import { ProfileType } from '@variables/profileType';

@Injectable()
export class HttpsRequestInterceptor implements HttpInterceptor {

  constructor(
    protected localstorageService: LocalstorageService,
    protected cache: CacheService,
    private readonly sessionService: SessionService,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Gate: block requests when session is expired to prevent API flooding
    // 401 handling is centralised in AccessSessionInterceptor
    if (this.sessionService.isExpired) {
      return EMPTY;
    }

    if (req.url === `${environment.apiVindi}/v1/public/payment_profiles`) {
      return next.handle(req.clone({
        setHeaders: { Authorization: 'Basic ' + btoa(environment.apiVindiKey + ':') },
      }));
    }

    if (req.headers.get('no-global-contractId') === 'true') {
      const token = localStorage.getItem('access_token') ?? '';
      return next.handle(req.clone({
        setHeaders: { Authorization: 'Bearer ' + token },
        headers: req.headers.set('contractId', req.headers.get('contractId') ?? ''),
      }));
    }

    const token = localStorage.getItem('access_token') ?? '';
    const dupReq = req.clone({
      setHeaders: { Authorization: 'Bearer ' + token },
      headers: req.headers.set('contractId', this.getContractId()),
    });

    const contractId = +dupReq.headers.get('contractId');
    const cacheExpiry = dupReq.headers.get('cache-expiry') ? +dupReq.headers.get('cache-expiry') : 300_000;

    if (this.isCachable(dupReq)) {
      const cached = this.cache.get(dupReq.url, cacheExpiry, contractId);
      return cached ? of(cached) : this.sendNewRequest(dupReq, next, contractId);
    }

    // Invalidate cache entry for mutating requests (POST/PUT/PATCH/DELETE)
    if (this.cache.get(dupReq.url, cacheExpiry, contractId)) {
      this.cache.delete(dupReq.url);
    }

    return next.handle(dupReq);
    // 401 on this path is handled downstream by AccessSessionInterceptor
  }

  private isCachable(req: HttpRequest<unknown>): boolean {
    return req.method === 'GET' && !!req.headers.get('cache-response');
  }

  private sendNewRequest(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    contractId: number,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      tap(event => this.cache.set(req.url, event, new Date(), contractId)),
    );
  }

  private getContractId(): string {
    const profile = this.localstorageService.getProfile();
    if (profile === ProfileType.CUSTOMER || profile === ProfileType.RELATIONSHIP) {
      return this.localstorageService.getContractId() ?? '0';
    }
    return this.localstorageService.getSessionStorageContractId();
  }
}
